import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import type { Connection, ConnectionSummary } from '@/shared/api/generated/catalog';
import { formatInstant } from '@/shared/model/instant';
import { StatusBadge } from '@/shared/ui/StatusBadge';

import { catalogueState, harvestActivity, scoredCoverage } from '../model/catalogue';
import type { CatalogueState } from '../model/catalogue';

/* One source, as the way into its catalogue.
 *
 * **Deliberately not `SourceCard`.** That card is about the *connection* — is it reachable, verify
 * it, harvest it, retire it — and it lives on the Connections screen where those are the actions. This
 * one is about the *catalogue behind* the connection: how much is in it, how much has been measured,
 * how current it is. Two cards for two questions, and the alternative was one card with two modes,
 * which is how a screen ends up with a Delete button on a browsing surface.
 *
 * **The whole card is a link and there are no action buttons**, which is the same decision stated
 * from the other side: nothing here can change anything, so a reader can click anywhere without
 * checking what is under the cursor first.
 *
 * **Every figure says whether it was measured.** `scored` may be null, the harvest may never have
 * run, and a running harvest's progress is a different fact from a finished one's totals — see the
 * presentation model, where each of those is its own case rather than a fallback to zero.
 */

/* The five catalogue states, mapped to a badge tone.
 *
 * A total map with `satisfies`, not a `switch` with a `default`. A sixth state added to
 * `CatalogueState` is then a compile error here rather than a row silently rendering in the
 * everything-is-fine tone — which is the defect this codebase records at `verdictTone` in two
 * separate features, both times found by review rather than by the type checker.
 */
const STATE_TONE = {
  neverHarvested: 'neutral',
  harvesting: 'info',
  current: 'ok',
  staleAfterFailure: 'warn',
  empty: 'neutral',
} satisfies Record<CatalogueState['kind'], 'neutral' | 'info' | 'ok' | 'warn'>;

export interface SourceCatalogueCardProps {
  readonly summary: ConnectionSummary;
  /** The connection itself, when the connections read has resolved it. `undefined` means the source
   * is in the summary and not in the connection listing — see the card body for what it renders. */
  readonly connection: Connection | undefined;
  /** `true` while the connection listing is loading or failed, which is why a name may be absent. */
  readonly namesUnread: boolean;
  /** How the connector family is spelled, and the address to show.
   *
   * **Passed in rather than imported, and the original reason for that is no longer the reason.**
   * The first version said `CONNECTOR_LABEL` lived in the connections feature and could not be
   * reached from here; it now lives in `@/entities/connection`, which this file may import freely,
   * so that argument is dead and saying it would be a claim about a boundary that does not exist.
   *
   * They stay props because the component reads better with no data source of its own: everything it
   * renders arrives as an argument, so it can be exercised against a plain object with no query
   * client and no i18n bridge beyond `t`. That is a real reason and a weaker one than the first, and
   * worth stating as such rather than leaving a false one in place.
   */
  readonly connectorLabel: (connection: Connection) => string;
  readonly address: (connection: Connection) => string;
  readonly to: string;
}

export function SourceCatalogueCard({
  summary,
  connection,
  namesUnread,
  connectorLabel,
  address,
  to,
}: SourceCatalogueCardProps): ReactElement {
  const { t } = useTranslation();
  const state = catalogueState(summary);
  const activity = harvestActivity(summary);
  const coverage = scoredCoverage(summary);

  return (
    <article className="lq-sourcecard">
      <Link className="lq-sourcecard__link" to={to}>
        <header className="lq-sourcecard__head">
          <div className="lq-sourcecard__identity">
            {/* A source with no name is a real state and gets a sentence rather than its id: the
                summary read and the connection listing are two requests, so one can arrive first,
                fail, or be capped while the other succeeded. Three causes, three sentences — the
                same rule `useDatasetNames`' consumers follow, because collapsing them puts a claim
                about the source on screen that only one of the three supports. */}
            <h3 className="lq-sourcecard__name">
              {connection !== undefined ? (
                connection.name
              ) : (
                <span className="lq-dim">
                  {namesUnread
                    ? t('catalogue.source.nameUnread')
                    : t('catalogue.source.unlisted')}
                </span>
              )}
            </h3>
            {connection !== undefined ? (
              <span className="lq-sourcecard__kind">{connectorLabel(connection)}</span>
            ) : null}
          </div>
          <StatusBadge
            tone={STATE_TONE[state.kind]}
            label={t(`catalogue.state.${state.kind}.label`)}
            title={t(`catalogue.state.${state.kind}.meaning`)}
          />
        </header>

        {connection !== undefined ? (
          <p className="lq-sourcecard__address lq-mono">{address(connection)}</p>
        ) : null}

        <dl className="lq-sourcecard__stats">
          <div>
            <dt>{t('catalogue.stat.datasets')}</dt>
            <dd className="lq-sourcecard__figure">{summary.datasets.toLocaleString()}</dd>
          </div>
          <div>
            <dt>{t('catalogue.stat.scored')}</dt>
            <dd className="lq-sourcecard__figure">
              {/* `—` for both non-measured cases, with the reason on hover. A `0` here is reserved
                  for the one case that genuinely means it: scoring was asked and found nothing,
                  which is a finding and should look like a number. */}
              {coverage.kind === 'measured' ? (
                coverage.covered.toLocaleString()
              ) : (
                <span
                  className="lq-dim"
                  title={t(`catalogue.scored.${coverage.kind}`)}
                  aria-label={t(`catalogue.scored.${coverage.kind}`)}
                >
                  —
                </span>
              )}
            </dd>
          </div>
          <div>
            <dt>{t('catalogue.stat.lastHarvest')}</dt>
            <dd className="lq-sourcecard__figure lq-sourcecard__figure--words">
              {/* A running harvest reports progress instead of a timestamp, because the timestamp
                  belongs to the *previous* run and putting it here while a new one is in flight
                  reads as the age of what is on screen. */}
              {activity.kind === 'running'
                ? t('catalogue.harvest.progress', {
                    observed: activity.observed,
                    discovered: activity.discovered,
                  })
                : activity.kind === 'starting'
                  ? t('catalogue.harvest.starting')
                  : summary.last_harvest_at === null
                    ? t('catalogue.harvest.never')
                    : /* **Formatted here and passed as a string, because the ICU bridge cannot
                           render a clock time.** `i18next-icu` 2.4.4 returns a message containing
                           `{x, time, short}` as its own source, braces and all — so a date-and-time
                           instant has to be assembled in the component, which is the same call
                           `catalogue.profile.at`'s neighbour on the profiler panel makes.

                           This cell previously handed a pre-formatted string to a message still
                           declaring `{at, date, medium}`, and an ICU `date` argument given a string
                           throws inside the bridge, which swallows it and renders the source. So
                           the field showed the literal `{at, date, medium}` rather than a wrong
                           date — the loud failure, luckily, but every reader of this card lost the
                           harvest time entirely.

                           Falls back to the raw instant rather than to a dash: a dash in this cell
                           is `catalogue.harvest.never`, and a formatting failure must not be able
                           to claim a source was never harvested. */
                      t('catalogue.harvest.ago', {
                        at:
                          formatInstant(summary.last_harvest_at) ??
                          summary.last_harvest_at,
                      })}
            </dd>
          </div>
        </dl>

        {/* What the state means for the reader's next step, in a sentence. The badge names the
            state; this says what to do about it, which is the half a badge cannot carry. */}
        <p className="lq-sourcecard__note">
          {t(`catalogue.state.${state.kind}.guidance`)}
        </p>
      </Link>
    </article>
  );
}
